import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Briefcase, MapPin, Clock, FileText, Upload, 
  CheckCircle, Loader2, Mail, Phone, Building2, Linkedin
} from "lucide-react";
import { toast } from "sonner";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export default function PublicJobApplicationPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [job, setJob] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    experience_years: "",
    current_company: "",
    linkedin_url: "",
    cover_letter: ""
  });
  
  const [cvFile, setCvFile] = useState(null);
  
  useEffect(() => {
    loadJob();
  }, [jobId]);
  
  const loadJob = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/public/jobs/${jobId}`);
      if (!response.ok) throw new Error("Job not found");
      const data = await response.json();
      setJob(data);
      setLoading(false);
    } catch (error) {
      toast.error("Job not found or no longer active");
      setLoading(false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload PDF or Word document");
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      
      setCvFile(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.experience_years) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (!cvFile) {
      toast.error("Please upload your CV");
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Prepare form data
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('experience_years', formData.experience_years);
      submitData.append('current_company', formData.current_company);
      submitData.append('linkedin_url', formData.linkedin_url);
      submitData.append('cover_letter', formData.cover_letter);
      submitData.append('cv_file', cvFile);
      
      const response = await fetch(`${backendUrl}/api/public/jobs/${jobId}/apply`, {
        method: 'POST',
        body: submitData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to submit application");
      }
      
      const result = await response.json();
      setSubmitted(true);
      toast.success(result.message);
      
    } catch (error) {
      toast.error(error.message);
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-bold mb-2">Job Not Found</h2>
            <p className="text-gray-600">This job is no longer accepting applications.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-2xl">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-3 text-gray-900">Application Submitted!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for applying to <strong>{job.title}</strong> at Securado.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900">
                <Mail className="w-4 h-4 inline mr-2" />
                Check your email for assessment links and next steps.
              </p>
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/branding/securado-logo-color.png" 
            alt="Securado" 
            className="h-10 mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{job.title}</h1>
          <div className="flex items-center justify-center gap-4 text-gray-600">
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {job.location}
            </span>
            <span className="flex items-center gap-1">
              <Briefcase className="w-4 h-4" />
              {job.employment_type}
            </span>
            {job.test_count > 0 && (
              <Badge className="bg-primary/10 text-primary">
                {job.test_count} Assessment{job.test_count > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Job Description */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>About the Role</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <p className="text-gray-700 whitespace-pre-line">{job.description}</p>
          </CardContent>
        </Card>
        
        {/* Application Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Submit Your Application
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="john@example.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="experience">Years of Experience *</Label>
                  <Input
                    id="experience"
                    type="number"
                    min="0"
                    max="50"
                    value={formData.experience_years}
                    onChange={(e) => handleInputChange('experience_years', e.target.value)}
                    placeholder="5"
                    required
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company">Current Company</Label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="company"
                      value={formData.current_company}
                      onChange={(e) => handleInputChange('current_company', e.target.value)}
                      placeholder="Acme Corp"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="linkedin">LinkedIn Profile</Label>
                  <div className="relative">
                    <Linkedin className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="linkedin"
                      value={formData.linkedin_url}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
              
              {/* Cover Letter */}
              <div>
                <Label htmlFor="cover_letter">Cover Letter / Why are you interested?</Label>
                <Textarea
                  id="cover_letter"
                  value={formData.cover_letter}
                  onChange={(e) => handleInputChange('cover_letter', e.target.value)}
                  placeholder="Tell us why you're a great fit for this role..."
                  rows={5}
                />
              </div>
              
              {/* CV Upload */}
              <div>
                <Label htmlFor="cv">Upload CV/Resume *</Label>
                <div className="mt-2">
                  <label
                    htmlFor="cv"
                    className="flex items-center justify-center gap-2 w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {cvFile ? cvFile.name : "Click to upload PDF or Word document (max 5MB)"}
                    </span>
                  </label>
                  <input
                    id="cv"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    required
                  />
                </div>
              </div>
              
              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting Application...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
              
              <p className="text-xs text-gray-500 text-center">
                By submitting, you agree to our data processing practices. 
                Your information will be used solely for recruitment purposes.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
